import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '../../../../components/PageHeader';
import { Pagination } from '../../../../components/pagination/Pagination';
import { IPaginationVariables, usePagination } from '../../../../hooks/usePagination';
import { LinkButton } from '../../../../components/button/ButtonLink';
import { Table } from 'components/table/Table';
import { formatDate } from '@utils/date';
import { useTrailer } from '../trailerContext';
import { decimateAndFormatNumber } from '@utils/number';
import { PAID_BY_OPTIONS } from './constants';
import { ConfirmDialog } from 'components/dialog/ConfirmDialog';
import toast from 'react-hot-toast';
import { getDisplayError } from '@utils/get-display-error';
import { captureException } from '@sentry/react';
import { deleteTrailerCost, getTrailerCosts } from '../../models';

const TABLE_HEADERS = [
  {
    id: 'id',
    name: 'ID',
  },
  {
    id: 'cost',
    name: 'Kost',
  },
  {
    id: 'description',
    name: 'Beschrijving',
  },
  {
    id: 'date',
    name: 'Datum',
  },
  {
    id: 'paidBy',
    name: 'Betaald door',
  },
];

export const TrailerCostsPage = () => {
  const { trailer } = useTrailer();
  const [refreshToken, setRefreshToken] = useState(0);
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getTrailerCosts(trailer.id, variables.cursor, variables.take);
    },
    [trailer],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 20,
    initialCursor: undefined,
    fetcher: pageFetcher,
    refreshToken,
  });

  return (
    <>
      <PageHeader title="Trailers" />

      <div>
        <div className="flex justify-end mb-4">
          <div>
            <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
              Nieuwe kost
            </LinkButton>
          </div>
        </div>

        <Table
          idKey="id"
          headers={TABLE_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(cost) => {
            return [
              <Link to={`${cost.id}`} className="link-text">
                {cost.id}
              </Link>,
              <div className="whitespace-nowrap">{`${decimateAndFormatNumber(cost.amount, 2)} EUR`}</div>,
              cost.description,
              formatDate(cost.date),
              PAID_BY_OPTIONS.find((v) => v.key === cost.paidBy)?.name ?? cost.paidBy,
              <ConfirmDialog
                triggerColor="danger"
                triggerText={<TrashIcon className="button-icon" />}
                title="Trailer kost verwijderen?"
                submitText="Verwijder"
                description={<div>Ben je zeker dat je deze trailer kost wilt verwijderen?</div>}
                onSubmit={async () => {
                  try {
                    await deleteTrailerCost(cost.id);
                    setRefreshToken((token) => token + 1);
                    toast.success('Trailerkost verwijderd');
                  } catch (err) {
                    captureException(err);
                    toast.error(`Kon trailer kost niet verwijderen: ${getDisplayError(err)}`);
                  }
                }}
              />,
            ];
          }}
        />

        <div className="my-4">
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        </div>
      </div>
    </>
  );
};
